import { useState, type FormEvent } from "react";

const topics = ["推荐看点 / 提供线索", "合作与内容转载", "反馈与建议", "其他事项"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") ?? "").trim();
    formData.delete("company_alt");
    if (honeypot) {
      setStatus("success");
      form.reset();
      setMessage("");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/daihs4rp14a1h3mr74h0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
        return;
      }
      setStatus("success");
      form.reset();
      setMessage("");
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络后重试。");
    }
  };

  const fieldClass =
    "w-full h-11 px-4 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors";

  return (
    <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-800 mb-2">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input id="contact-name" name="name" type="text" required autoComplete="name" placeholder="怎么称呼你" className={fieldClass} />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-800 mb-2">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input id="contact-email" name="email" type="email" required autoComplete="email" placeholder="your@email.com" className={fieldClass} />
        </div>
      </div>

      <div>
        <label htmlFor="contact-topic" className="block text-sm text-foreground-800 mb-2">
          事项类型
        </label>
        <select id="contact-topic" name="topic" defaultValue={topics[0]} className={`${fieldClass} cursor-pointer`}>
          {topics.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm text-foreground-800 mb-2">
          留言内容 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={500}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="想推荐的看点、想反馈的问题，或者合作意向，都可以写在这里"
          className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-300 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors resize-none"
        />
        <div className="mt-1.5 text-right text-xs text-foreground-500">{message.length} / 500</div>
      </div>

      <input
        type="text"
        name="company_alt"
        className="form-field-aux"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>提交留言
            </>
          )}
        </button>

        {status === "success" && (
          <p className="text-sm text-secondary-700 flex items-center gap-1.5">
            <i className="ri-checkbox-circle-line"></i>留言已提交，我们会尽快回复你。
          </p>
        )}
        {status === "error" && (
          <p className="text-sm text-primary-600 flex items-center gap-1.5">
            <i className="ri-error-warning-line"></i>
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}